import { DateTime } from 'luxon';
import {
  action,
  computed,
  type IReactionDisposer,
  makeObservable,
  observable,
  reaction,
  runInAction,
} from 'mobx';

import { caldavApi, CalDavApiError } from '@/api/caldavApi';
import { timelogsApi } from '@/api/timelogsApi';
import { yandexTrackerApi } from '@/api/yandexTrackerApi';
import type { AlertsStore } from '@/configs/store/AlertsStore';
import { QueryStore } from '@/configs/store/QueryStore/QueryStore';
import { ViewTable } from '@/configs/ViewTable';
import type { CalDavCalendar } from '@/features/caldav/types';
import { fetchUserLogs, LogsMode } from '@/features/logsMode';
import { DateNavigationModel } from '@/models/DateNavigationModel';
import type { DraftTimelog, WeeklyWorkLog } from '@/types';
import { formatDurationRussian } from '@/utils/formatDurationRussian';
import { parseDuration } from '@/utils/parseDuration';

import { mockCalendars, mockEvents } from '../mocks/mockEvents';
import type { EventAttendee } from '../types';
import type {
  CalendarEventWithColor,
  CalendarViewStatus,
  CollapsedLogGroup,
  LogFormDefaults,
  WeekViewData,
} from '../types';
import { buildLogFormDefaults } from '../utils/buildLogFormDefaults';
import { buildWeekViewData } from '../utils/eventLayout';

import {
  CalendarPreferencesModel,
  CalendarUIStateModel,
  CalendarVisibilityModel,
  DraftsLayoutModel,
  OtherLogsLayoutModel,
  WorklogsModel,
} from './models';

export class CalendarViewStore {
  status: CalendarViewStatus = 'idle';
  weekData: WeekViewData | null = null;
  calendars: CalDavCalendar[] = [];
  error: string | null = null;
  private _isLoadingEvents = false;
  private _isSyncing = false;
  private _isRefreshing = false;
  private _drafts: DraftTimelog[] = [];
  private _userId: string | null = null;
  private _attendeesCache = new Map<string, EventAttendee[]>();
  private _attendeesLoadingIds = new Set<string>();

  readonly dateNavigation: DateNavigationModel;
  private readonly _queryStore: QueryStore;
  private readonly _mode: LogsMode;
  private _dateReactionDisposer: IReactionDisposer | null = null;

  readonly preferences: CalendarPreferencesModel;
  readonly visibility: CalendarVisibilityModel;
  readonly worklogs: WorklogsModel;
  readonly uiState: CalendarUIStateModel;
  private readonly _otherLogsLayout: OtherLogsLayoutModel;
  private readonly _draftsLayout: DraftsLayoutModel;

  constructor(queryStore: QueryStore, alertsStore: AlertsStore, mode: LogsMode = LogsMode.legacy) {
    this._queryStore = queryStore;
    this._mode = mode;

    this.preferences = new CalendarPreferencesModel(alertsStore);
    this.visibility = new CalendarVisibilityModel();
    this.worklogs = new WorklogsModel();
    this.uiState = new CalendarUIStateModel();
    this._otherLogsLayout = new OtherLogsLayoutModel();
    this._draftsLayout = new DraftsLayoutModel();

    const initialDate = this._parseDateFromUrl();
    this.dateNavigation = new DateNavigationModel(initialDate, {
      getView: () => ViewTable.Week,
      setView: () => {},
    });
    this._syncDateToUrl();

    makeObservable<
      CalendarViewStore,
      | '_drafts'
      | '_isSyncing'
      | '_isRefreshing'
      | '_isLoadingEvents'
      | '_attendeesCache'
      | '_attendeesLoadingIds'
    >(this, {
      status: observable,
      weekData: observable,
      calendars: observable,
      error: observable,
      _drafts: observable,
      _isSyncing: observable,
      _isRefreshing: observable,
      _isLoadingEvents: observable,
      _attendeesCache: observable,
      _attendeesLoadingIds: observable,
      isBusy: computed,
      isLoadingEvents: computed,
      isLoading: computed,
      isCaldavNotConfigured: computed,
      isNoCalendarsSelected: computed,
      isError: computed,
      dailyWorkMinutes: computed,
      filteredWeekData: computed,
      unmatchedWorklogs: computed,
      weekDataWithOtherLogs: computed,
      weekDataWithDrafts: computed,
      sidebarCollapsed: computed,
      showOtherLogs: computed,
      showDrafts: computed,
      selectedEvent: computed,
      selectedLogGroup: computed,
      editingLog: computed,
      selectedDraft: computed,
      logFormEvent: computed,
      logFormDefaults: computed,
      logFormTaskLabel: computed,
      logFormForAll: computed,
      quickAddDate: computed,
      dragLogData: computed,
      dragCreateDraftData: computed,
      selectedEventAttendees: computed,
      isAttendeesLoading: computed,
      init: action.bound,
      loadWeekData: action.bound,
      selectEvent: action.bound,
      clearSelectedEvent: action.bound,
      openLogForm: action.bound,
      closeLogForm: action.bound,
      openQuickAdd: action.bound,
      closeQuickAdd: action.bound,
      addCreatedWorklog: action.bound,
      deleteWorklog: action.bound,
      toggleCalendarVisibility: action.bound,
      toggleSidebar: action.bound,
      toggleOtherLogs: action.bound,
      toggleShowDrafts: action.bound,
      selectLogGroup: action.bound,
      clearLogGroup: action.bound,
      openEditLog: action.bound,
      closeEditLog: action.bound,
      openDragLog: action.bound,
      closeDragLog: action.bound,
      setDrafts: action.bound,
      openDraftEdit: action.bound,
      closeDraftEdit: action.bound,
      openDragCreateDraft: action.bound,
      closeDragCreateDraft: action.bound,
      refreshData: action.bound,
      syncAndRefresh: action.bound,
    });

    this._dateReactionDisposer = reaction(
      () => this.dateNavigation.currentDate.toISODate(),
      () => {
        this.clearLogGroup();
        this._syncDateToUrl();
        this.loadWeekData();
      },
    );
  }

  get isLoading(): boolean {
    return this.status === 'loading';
  }

  get isLoadingEvents(): boolean {
    return this._isLoadingEvents;
  }

  get isCaldavNotConfigured(): boolean {
    return this.status === 'caldav_not_configured';
  }

  get isNoCalendarsSelected(): boolean {
    return this.status === 'no_calendars_selected';
  }

  get isError(): boolean {
    return this.status === 'error';
  }

  get isBusy(): boolean {
    return this._isSyncing || this._isRefreshing;
  }

  get sidebarCollapsed(): boolean {
    return this.preferences.sidebarCollapsed;
  }

  get showOtherLogs(): boolean {
    return this.preferences.showOtherLogs;
  }

  get showDrafts(): boolean {
    return this.preferences.showDrafts;
  }

  get selectedEvent(): CalendarEventWithColor | null {
    return this.uiState.selectedEvent;
  }

  get selectedLogGroup(): CollapsedLogGroup | null {
    return this.uiState.selectedLogGroup;
  }

  get editingLog(): WeeklyWorkLog | null {
    return this.uiState.editingLog;
  }

  get selectedDraft(): DraftTimelog | null {
    return this.uiState.selectedDraft;
  }

  get logFormEvent(): CalendarEventWithColor | null {
    return this.uiState.logFormEvent;
  }

  get logFormDefaults(): LogFormDefaults | null {
    return this.uiState.logFormDefaults;
  }

  get logFormTaskLabel(): string {
    return this.uiState.logFormTaskLabel;
  }

  get logFormForAll(): boolean {
    return this.uiState.logFormForAll;
  }

  get quickAddDate(): DateTime | null {
    return this.uiState.quickAddDate;
  }

  get dragLogData(): { date: DateTime; startTime: string; duration: string } | null {
    return this.uiState.dragLogData;
  }

  get dragCreateDraftData(): { date: DateTime; startTime: string; duration: string } | null {
    return this.uiState.dragCreateDraftData;
  }

  get selectedEventAttendees(): EventAttendee[] {
    if (!this.uiState.selectedEvent) return [];

    return this._attendeesCache.get(this.uiState.selectedEvent.id) ?? [];
  }

  get isAttendeesLoading(): boolean {
    if (!this.uiState.selectedEvent) return false;

    return this._attendeesLoadingIds.has(this.uiState.selectedEvent.id);
  }

  get dailyWorkMinutes(): Record<string, number> {
    return this.worklogs.dailyWorkMinutes;
  }

  get unmatchedWorklogs(): WeeklyWorkLog[] {
    return this._otherLogsLayout.computeUnmatchedWorklogs(
      this.weekData,
      this.worklogs.weeklyWorklogs,
    );
  }

  get filteredWeekData(): WeekViewData | null {
    if (!this.weekData) return null;

    const hasHidden = this.calendars.some((c) => !this.visibility.isVisible(c.id));

    if (!hasHidden) return this.weekData;

    const filteredEvents: CalendarEventWithColor[] = [];

    for (const day of this.weekData.days) {
      for (const pe of day.timedEvents) {
        if (this.visibility.isVisible(pe.event.calendarId)) {
          filteredEvents.push(pe.event);
        }
      }

      for (const e of day.allDayEvents) {
        if (this.visibility.isVisible(e.calendarId)) {
          filteredEvents.push(e);
        }
      }
    }

    return buildWeekViewData(filteredEvents, this.calendars, this.weekData.weekStart);
  }

  get weekDataWithOtherLogs(): WeekViewData | null {
    const base = this.filteredWeekData;

    if (!base) return null;

    if (!this.showOtherLogs) return base;

    const worklogs = this.unmatchedWorklogs;

    if (worklogs.length === 0) return base;

    return this._otherLogsLayout.buildWeekDataWithOtherLogs(base, worklogs, this.calendars);
  }

  get weekDataWithDrafts(): WeekViewData | null {
    const base = this.weekDataWithOtherLogs;

    if (!base) return null;

    if (!this.showDrafts || this._drafts.length === 0) return base;

    const weekStart = this.dateNavigation.currentDate.startOf('week');
    const draftEvents = this._draftsLayout.buildDraftPseudoEvents(this._drafts, weekStart);

    if (draftEvents.length === 0) return base;

    const allEvents: CalendarEventWithColor[] = [];

    for (const day of base.days) {
      for (const pe of day.timedEvents) {
        allEvents.push(pe.event);
      }

      for (const e of day.allDayEvents) {
        allEvents.push(e);
      }
    }

    return buildWeekViewData([...allEvents, ...draftEvents], this.calendars, base.weekStart);
  }

  toggleSidebar(): void {
    this.preferences.toggleSidebar();
  }

  toggleOtherLogs(): void {
    this.preferences.toggleOtherLogs();
  }

  toggleShowDrafts(): void {
    this.preferences.toggleShowDrafts();
  }

  isCalendarVisible(calendarId: string): boolean {
    return this.visibility.isVisible(calendarId);
  }

  toggleCalendarVisibility(calendarId: string): void {
    this.visibility.toggle(calendarId);
  }

  selectEvent(event: CalendarEventWithColor): void {
    this.uiState.selectEvent(event);
    void this._loadAttendeesForEvent(event.id);
  }

  clearSelectedEvent(): void {
    this.uiState.clearSelectedEvent();
  }

  selectLogGroup(group: CollapsedLogGroup): void {
    this.uiState.selectLogGroup(group);
  }

  clearLogGroup(): void {
    this.uiState.clearLogGroup();
  }

  openEditLog(log: WeeklyWorkLog): void {
    this.uiState.openEditLog(log);
  }

  closeEditLog(): void {
    this.uiState.closeEditLog();
  }

  openQuickAdd(date: DateTime): void {
    this.uiState.openQuickAdd(date);
  }

  closeQuickAdd(): void {
    this.uiState.closeQuickAdd();
  }

  openDragLog(date: DateTime, startTime: string, duration: string): void {
    this.uiState.openDragLog(date, startTime, duration);
  }

  closeDragLog(): void {
    this.uiState.closeDragLog();
  }

  openDraftEdit(draft: DraftTimelog): void {
    this.uiState.openDraftEdit(draft);
  }

  closeDraftEdit(): void {
    this.uiState.closeDraftEdit();
  }

  openDragCreateDraft(date: DateTime, startTime: string, duration: string): void {
    this.uiState.openDragCreateDraft(date, startTime, duration);
  }

  closeDragCreateDraft(): void {
    this.uiState.closeDragCreateDraft();
  }

  closeLogForm(): void {
    this.uiState.clearLogFormState();
  }

  addCreatedWorklog(
    worklogId: string,
    durationMinutes: number,
    taskKey: string,
    start: string,
  ): void {
    this.worklogs.addPending(worklogId, { duration: durationMinutes, taskKey, start });
  }

  async init(): Promise<void> {
    this.status = 'loading';
    this.error = null;

    try {
      const statusResponse = await caldavApi.getStatus();

      if (statusResponse.status !== 'active') {
        runInAction(() => {
          this.calendars = mockCalendars;
          this._loadMockWeekData();
          this.status = 'caldav_not_configured';
        });

        return;
      }

      const calendars = await caldavApi.getCalendars();

      runInAction(() => {
        this.calendars = calendars;
      });

      const selectedCalendars = calendars.filter((c) => c.isSelected);

      if (selectedCalendars.length === 0) {
        runInAction(() => {
          this._loadEmptyWeekData();
          this.status = 'no_calendars_selected';
        });

        return;
      }

      await this._fetchEventsFromApi();

      runInAction(() => {
        this.status = 'ready';
      });
    } catch (e) {
      runInAction(() => {
        this.status = 'error';
        this.error = e instanceof Error ? e.message : 'Unknown error';
      });
    }
  }

  async loadWeekData(): Promise<void> {
    if (this.status === 'caldav_not_configured') {
      this._loadMockWeekData();

      return;
    }

    if (this.status === 'no_calendars_selected') {
      this._loadEmptyWeekData();

      return;
    }

    if (this.status === 'ready') {
      await this._fetchEventsFromApi();
    }
  }

  async refreshData(): Promise<void> {
    if (this.isBusy) return;

    this.clearLogGroup();
    this._isRefreshing = true;

    try {
      await this._fetchEventsFromApi();
    } finally {
      runInAction(() => {
        this._isRefreshing = false;
      });
    }
  }

  async syncAndRefresh(): Promise<void> {
    if (this.isBusy) return;

    this._isSyncing = true;

    try {
      await caldavApi.syncEvents();
      await this._fetchEventsFromApi();
    } finally {
      runInAction(() => {
        this._isSyncing = false;
      });
    }
  }

  async openLogForm(event: CalendarEventWithColor, forAll = true): Promise<void> {
    if (!this._attendeesCache.has(event.id)) {
      await this._loadAttendeesForEvent(event.id);
    }

    const cachedAttendees = this._attendeesCache.get(event.id) ?? [];

    const defaults = buildLogFormDefaults(event);
    defaults.attendees = cachedAttendees.filter(
      (a) => a.isKnownUser && a.responseStatus !== 'DECLINED',
    );
    let taskLabel = '';

    if (event.worklogId) {
      const wl = this.worklogs.getWorklog(event.worklogId);

      if (wl) {
        defaults.taskKey = wl.taskKey;
        defaults.duration = formatDurationRussian(parseDuration(wl.duration));
        defaults.comment = wl.comment ?? '';
        const wlStart = DateTime.fromISO(wl.start);

        if (wlStart.isValid) {
          defaults.startDate = wlStart.toISODate();
          defaults.startTime = wlStart.toFormat('HH:mm');
        }
      } else {
        const pending = this.worklogs.getPending(event.worklogId);

        if (pending) {
          defaults.taskKey = pending.taskKey;
        }
      }
    }

    if (defaults.taskKey) {
      try {
        const matched =
          this._mode === LogsMode.v2
            ? await timelogsApi.getTask(defaults.taskKey)
            : (await yandexTrackerApi.searchAllTasks(defaults.taskKey)).find(
                (t) => t.key === defaults.taskKey,
              );

        if (matched) {
          taskLabel = `${matched.key} — ${matched.summary}`;
        }
      } catch {
        // Ошибка suggest — оставляем taskLabel пустым
      }
    }

    runInAction(() => {
      this.uiState.setLogFormState(event, defaults, taskLabel, forAll);
    });
  }

  async deleteWorklog(event: CalendarEventWithColor): Promise<void> {
    if (!event.worklogId) return;

    const worklog = this.worklogs.getWorklog(event.worklogId);
    const pending = this.worklogs.getPending(event.worklogId);
    const taskKey = worklog?.taskKey ?? pending?.taskKey;

    if (this._mode === LogsMode.v2) {
      // Привязка к встрече хранится в самом логе: удалили лог — нет и привязки
      await timelogsApi.deleteTimelog(event.worklogId);
    } else {
      if (taskKey) {
        await yandexTrackerApi.deleteWorkLog({
          taskKey,
          workLogId: event.worklogId,
        });
      }

      await caldavApi.unlinkWorklog(event.id);
    }

    this.worklogs.removePending(event.worklogId);
    await this.loadWeekData();

    runInAction(() => {
      this.clearSelectedEvent();
    });
  }

  setDrafts(drafts: DraftTimelog[]): void {
    this._drafts = drafts;
  }

  setUserId(userId: string): void {
    this._userId = userId;
  }

  private _loadMockWeekData(): void {
    const weekStart = this.dateNavigation.currentDate.startOf('week');
    this.weekData = buildWeekViewData(mockEvents, mockCalendars, weekStart);
  }

  private _loadEmptyWeekData(): void {
    const weekStart = this.dateNavigation.currentDate.startOf('week');
    this.weekData = buildWeekViewData([], this.calendars, weekStart);
  }

  private async _loadAttendeesForEvent(eventId: string): Promise<void> {
    if (this._attendeesCache.has(eventId) || this._attendeesLoadingIds.has(eventId)) return;

    runInAction(() => {
      this._attendeesLoadingIds.add(eventId);
    });

    try {
      const attendees = await caldavApi.getEventAttendees(eventId);
      runInAction(() => {
        this._attendeesCache.set(eventId, attendees);
      });
    } catch {
      // Attendees will simply not be shown
    } finally {
      runInAction(() => {
        this._attendeesLoadingIds.delete(eventId);
      });
    }
  }

  private async _fetchEventsFromApi(): Promise<void> {
    if (this._isLoadingEvents) {
      return;
    }

    this._isLoadingEvents = true;

    try {
      this._attendeesCache.clear();

      const weekStart = this.dateNavigation.currentDate.startOf('week');
      const weekEnd = weekStart.plus({ days: 6 });

      const [caldavEvents, worklogsData] = await Promise.all([
        caldavApi.getEvents(weekStart.toISODate()!, weekEnd.toISODate()!),
        this._userId
          ? fetchUserLogs({
              mode: this._mode,
              userId: this._userId,
              from: weekStart.startOf('day').toISO()!,
              to: weekStart.plus({ days: 6 }).endOf('day').toISO()!,
            })
          : Promise.resolve([]),
      ]);

      // worklogId от CalDAV — id лога в Трекере; в нашем учёте привязку хранит сам лог
      const events =
        this._mode === LogsMode.v2
          ? caldavEvents.map((event) => ({
              ...event,
              worklogId:
                worklogsData.find(
                  ({ calendarEvent }) =>
                    calendarEvent?.uid === event.externalId &&
                    (calendarEvent.recurrenceId ?? null) === (event.recurrenceId ?? null),
                )?.id ?? null,
            }))
          : caldavEvents;

      runInAction(() => {
        this.worklogs.setWorklogs(worklogsData);

        const enrichedEvents = events.map((event) => {
          if (!event.worklogId) return event;

          const wl = this.worklogs.getWorklog(event.worklogId);

          if (wl) {
            return {
              ...event,
              matchedWorklog: {
                duration: parseDuration(wl.duration),
                taskKey: wl.taskKey,
                start: wl.start,
                comment: wl.comment,
              },
            };
          }

          const pending = this.worklogs.getPending(event.worklogId);

          if (pending) {
            return {
              ...event,
              matchedWorklog: {
                duration: pending.duration,
                taskKey: pending.taskKey,
                start: pending.start,
              },
            };
          }

          return event;
        });

        this.weekData = buildWeekViewData(enrichedEvents, this.calendars, weekStart);
      });
    } catch (error) {
      if (error instanceof CalDavApiError && error.code === 'caldav_not_configured') {
        runInAction(() => {
          this.calendars = mockCalendars;
          this._loadMockWeekData();
          this.status = 'caldav_not_configured';
        });
      } else {
        throw error;
      }
    } finally {
      this._isLoadingEvents = false;
    }
  }

  private _parseDateFromUrl(): DateTime {
    let param = new URLSearchParams(window.location.search).get('date_from');

    if (!param) {
      param = this._queryStore.getParam('date_from');
    }

    if (param) {
      const parsed = DateTime.fromISO(param);

      if (parsed.isValid) {
        return parsed.startOf('week');
      }
    }

    return DateTime.local().startOf('week');
  }

  private _syncDateToUrl(): void {
    const startOfWeek = this.dateNavigation.currentDate.startOf('week');
    const endOfWeek = startOfWeek.plus({ days: 6 });
    this._queryStore.setParam('date_from', startOfWeek.toISODate());
    this._queryStore.setParam('date_to', endOfWeek.toISODate());
  }

  destroy(): void {
    this._dateReactionDisposer?.();
    this._dateReactionDisposer = null;
  }
}
